import React from 'react';
import './PriceRevalidationModal.css';

function PriceRevalidationModal({ isOpen, onClose, revalidationData }) {
  if (!isOpen || !revalidationData) return null;

  // Check if this is batch API response (has flight_options array) or single response
  const isBatchResponse = revalidationData.flight_options && Array.isArray(revalidationData.flight_options);

  const getStatusBadge = (status) => {
    const badges = {
      'available': { text: 'Available', color: '#4caf50' },
      'price_changed': { text: 'Price Changed', color: '#ff9800' },
      'unavailable': { text: 'Unavailable', color: '#f44336' }
    };
    const badge = badges[status] || { text: status, color: '#999' };
    return <span className="status-badge" style={{ backgroundColor: badge.color }}>{badge.text}</span>;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    // Handle both string and number formats
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Price Revalidation Results</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {isBatchResponse ? (
            <>
              {/* Batch API Response */}
              <div className="summary-section">
                <div className="summary-item">
                  <span className="summary-label">Flight Options:</span>
                  <span className="summary-value">{revalidationData.total_flight_options || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Price Options Checked:</span>
                  <span className="summary-value">{revalidationData.total_price_options_checked || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Available:</span>
                  <span className="summary-value" style={{ color: '#4caf50' }}>{revalidationData.total_available || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Unavailable:</span>
                  <span className="summary-value" style={{ color: '#f44336' }}>{revalidationData.total_unavailable || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Price Changed:</span>
                  <span className="summary-value" style={{ color: '#ff9800' }}>{revalidationData.total_price_changed || 0}</span>
                </div>
                {revalidationData.errors_count > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">Errors:</span>
                    <span className="summary-value" style={{ color: '#f44336' }}>{revalidationData.errors_count}</span>
                  </div>
                )}
              </div>

              <div className="results-section">
                <h3>Flight Options Details:</h3>
                {revalidationData.flight_options.map((flightOption, foIndex) => (
                  <div key={flightOption.flight_option_id || foIndex} className="flight-option-group">
                    <div className="flight-option-header">
                      <h4>Flight Option #{flightOption.flight_option_id}</h4>
                      {flightOption.error_message && (
                        <div className="error-message" style={{ marginTop: '10px' }}>
                          <strong>Error:</strong> {flightOption.error_message}
                        </div>
                      )}
                    </div>

                    {flightOption.results && flightOption.results.length > 0 && (
                      <div className="price-options-list">
                        {flightOption.results.map((result, index) => (
              <div key={result.price_option_id || index} className="result-card">
                <div className="result-header">
                  <span className="price-option-id">Price Option #{result.price_option_id}</span>
                  {getStatusBadge(result.status)}
                </div>

                <div className="price-comparison">
                  <div className="price-row">
                    <span className="price-label">Original Price:</span>
                    <span className="price-value">{formatPrice(result.original_total_price)}</span>
                  </div>
                  {result.status === 'price_changed' && (
                    <>
                      <div className="price-row highlight">
                        <span className="price-label">New Price:</span>
                        <span className="price-value new-price">{formatPrice(result.current_total_price)}</span>
                      </div>
                      <div className="price-row">
                        <span className="price-label">Price Difference:</span>
                        <span className="price-value" style={{
                          color: result.current_total_price > result.original_total_price ? '#f44336' : '#4caf50'
                        }}>
                          {result.current_total_price > result.original_total_price ? '+' : ''}
                          {formatPrice(result.current_total_price - result.original_total_price)}
                        </span>
                      </div>
                      {result.new_price_option_id && (
                        <div className="price-row">
                          <span className="price-label">New Price Option ID:</span>
                          <span className="price-value">{result.new_price_option_id}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {result.segments_details && result.segments_details.length > 0 && (
                  <div className="segments-section">
                    <h4>Flight Segments:</h4>
                    {result.segments_details.map((segment, segIndex) => (
                      <div key={segIndex} className="segment-item">
                        <span className="segment-flight">{segment.flight_number}</span>
                        <span className="segment-route">
                          {segment.departure_airport} → {segment.arrival_airport}
                        </span>
                        <span className="segment-class">Class: {segment.booking_class}</span>
                        <span className={`segment-status ${segment.available ? 'available' : 'unavailable'}`}>
                          {segment.available ? '✓' : '✗'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {result.error_message && (
                  <div className="error-message">
                    <strong>Error:</strong> {result.error_message}
                  </div>
                )}
              </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Single API Response (original format) */}
              <div className="summary-section">
                <div className="summary-item">
                  <span className="summary-label">Checked:</span>
                  <span className="summary-value">{revalidationData.checked_count}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Available:</span>
                  <span className="summary-value" style={{ color: '#4caf50' }}>{revalidationData.available_count}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Unavailable:</span>
                  <span className="summary-value" style={{ color: '#f44336' }}>{revalidationData.unavailable_count}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Price Changed:</span>
                  <span className="summary-value" style={{ color: '#ff9800' }}>{revalidationData.price_changed_count}</span>
                </div>
              </div>

              <div className="results-section">
                <h3>Details:</h3>
                {revalidationData.results && revalidationData.results.map((result, index) => (
                  <div key={result.price_option_id || index} className="result-card">
                    <div className="result-header">
                      <span className="price-option-id">Price Option #{result.price_option_id}</span>
                      {getStatusBadge(result.status)}
                    </div>

                    <div className="price-comparison">
                      <div className="price-row">
                        <span className="price-label">Original Price:</span>
                        <span className="price-value">{formatPrice(result.original_total_price)}</span>
                      </div>
                      {result.status === 'price_changed' && (
                        <>
                          <div className="price-row highlight">
                            <span className="price-label">New Price:</span>
                            <span className="price-value new-price">{formatPrice(result.current_total_price)}</span>
                          </div>
                          <div className="price-row">
                            <span className="price-label">Price Difference:</span>
                            <span className="price-value" style={{
                              color: result.current_total_price > result.original_total_price ? '#f44336' : '#4caf50'
                            }}>
                              {result.current_total_price > result.original_total_price ? '+' : ''}
                              {formatPrice(result.current_total_price - result.original_total_price)}
                            </span>
                          </div>
                          {result.new_price_option_id && (
                            <div className="price-row">
                              <span className="price-label">New Price Option ID:</span>
                              <span className="price-value">{result.new_price_option_id}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {result.segments_details && result.segments_details.length > 0 && (
                      <div className="segments-section">
                        <h4>Flight Segments:</h4>
                        {result.segments_details.map((segment, segIndex) => (
                          <div key={segIndex} className="segment-item">
                            <span className="segment-flight">{segment.flight_number}</span>
                            <span className="segment-route">
                              {segment.departure_airport} → {segment.arrival_airport}
                            </span>
                            <span className="segment-class">Class: {segment.booking_class}</span>
                            <span className={`segment-status ${segment.available ? 'available' : 'unavailable'}`}>
                              {segment.available ? '✓' : '✗'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {result.error_message && (
                      <div className="error-message">
                        <strong>Error:</strong> {result.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="close-footer-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default PriceRevalidationModal;
